import {
    getOrganizationClauseChecklists,
    IClause,
    IOrganization,
    IOrganizationClauseChecklist,
} from "@/api";
import { ClauseChecklistTitleCell } from "@/components/admin/table/cells/ClauseChecklistTitleCell";
import { SharedWithGroupLabel } from "@/components/shared/SharedWithGroupLabel";
import { DateFilterInput } from "@/components/shared/table/DateFilterInput";
import { Table } from "@/components/shared/table/Table";
import { useFilterModel } from "@/hooks/useFilterModel";
import { formatDate, handleRowSelection } from "@/utils";
import { Box, Stack, Typography } from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { CreateNewChecklistsButton } from "../buttons/CreateNewChecklistsButton";
import { OrganizationClauseChecklistsActions } from "../buttons/OrganizationClauseChecklistsActions";
import { RemoveClauseChecklistFromOrganization } from "../cells/RemoveClauseChecklistFromOrganization";

interface IProps {
    /**
     * the organization to display the clause checklists for
     */
    organization: IOrganization;
}

/**
 * a table that displays the clause checklists for an organization
 */
export const ClauseChecklistsTable = ({ organization }: IProps) => {
    const columns: GridColDef<IOrganizationClauseChecklist>[] = useMemo(
        () => [
            {
                field: "name",
                headerName: "Title",
                display: "flex",
                renderCell: ({ row }) => (
                    <Stack minWidth={0}>
                        <ClauseChecklistTitleCell clauseChecklist={row} />
                        {row.is_shared_via_group && (
                            <SharedWithGroupLabel
                                group={row.organization_group}
                            />
                        )}
                    </Stack>
                ),
            },
            {
                field: "description",
                headerName: "Description",
                display: "flex",
                maxWidth: 300,
            },
            {
                field: "updated_at",
                headerName: "Last Edited",
                display: "flex",
                type: "date",
                filterOperators: [
                    {
                        label: "Date is",
                        value: "dateIs",
                        getApplyFilterFn: (filterItem) => {
                            if (!filterItem.value) {
                                return null;
                            }
                            return ({ value }) => {
                                return value === filterItem.value;
                            };
                        },
                        InputComponent: DateFilterInput,
                    },
                ],
                valueFormatter: (value: string) => {
                    return formatDate(value, "PPp");
                },
            },
            {
                field: "clauses",
                headerName: "Clauses",
                display: "flex",
                sortable: false,
                filterable: false,
                valueFormatter: (value: IClause[]) => {
                    return value.length;
                },
            },
            {
                field: "actions",
                headerName: "Actions",
                type: "actions",
                getActions: ({ row }) =>
                    row.is_directly_assigned
                        ? [
                              <RemoveClauseChecklistFromOrganization
                                  organizationId={organization.id}
                                  clauseChecklistId={row.id}
                              />,
                          ]
                        : [],
            },
        ],
        [organization.id]
    );

    const [selectedClauseChecklistsIds, setSelectedClauseChecklistsIds] =
        useState<number[]>([]);

    const handleRowSelectionModelChange = (
        rowSelectionModel: GridRowSelectionModel
    ) => {
        setSelectedClauseChecklistsIds(
            handleRowSelection(rowSelectionModel, rows)
        );
    };

    const [rows, setRows] = useState<IOrganizationClauseChecklist[]>([]);
    const { filterModel, handleFilterModelChange } = useFilterModel();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const autoSizeOptions = {
        columns: ["name", "description", "updated_at", "clauses", "actions"],
        includeHeaders: true,
        expand: true,
    };

    // Fetch Clause Checklists from the API
    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-organization-details-clause-checklists",
            organization.id,
            paginationModel.page,
            paginationModel.pageSize,
            filterModel,
        ],
        queryFn: () =>
            getOrganizationClauseChecklists(
                organization.id,
                paginationModel.page + 1,
                paginationModel.pageSize,
                filterModel
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    // memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);
    return (
        <Box sx={{ mt: 3 }}>
            <Stack direction="row" justifyContent="space-between" mx={2}>
                <Typography variant="h6">Clause Checklists</Typography>
                <Stack direction="row" gap={0.5}>
                    <CreateNewChecklistsButton organization={organization} />
                    <OrganizationClauseChecklistsActions
                        selectedClauseChecklistIds={selectedClauseChecklistsIds}
                        rows={rows}
                        organization={organization}
                    />
                </Stack>
            </Stack>
            <Box sx={{ mt: 2, mx: 1 }}>
                <Table
                    loading={isFetching}
                    columns={columns}
                    rows={rows}
                    checkboxSelection
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    disableColumnResize
                    autosizeOptions={autoSizeOptions}
                    autoSize
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    rowCount={rowCount}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode={"server"}
                    filterMode={"server"}
                    sortingMode={"server"}
                    onFilterModelChange={handleFilterModelChange}
                    onSortModelChange={handleFilterModelChange}
                />
            </Box>
        </Box>
    );
};
